import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { format, parseISO } from 'date-fns'
import type { PontoDiario } from './ColunasDiarias'

type Serie = { titulo: string; pontos: PontoDiario[]; formatar: (v: number) => string }
type Coluna = { chave: string; nome: string }

/**
 * A versão em tabela dos gráficos diários: todo valor que o gráfico mostra também é
 * lido aqui, sem depender de passar o mouse. Mais recente primeiro.
 * Com mais de uma coluna por série, entra também o total.
 */
export function TabelaDiaria({ series, colunas }: { series: Serie[]; colunas: Coluna[] }) {
  const dias = series[0]?.pontos.map((p) => p.data) ?? []
  const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
  const comTotal = colunas.length > 1
  const valor = (p: PontoDiario | undefined, chave: string) => Number(p?.[chave] ?? 0)

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={comTotal ? 2 : 1}>Data</TableCell>
            {series.map((s) =>
              comTotal ? (
                <TableCell key={s.titulo} align="center" colSpan={colunas.length + 1}>
                  {s.titulo}
                </TableCell>
              ) : (
                <TableCell key={s.titulo} align="right">
                  {s.titulo}
                </TableCell>
              ),
            )}
          </TableRow>
          {comTotal ? (
            <TableRow>
              {series.map((s) => [
                ...colunas.map((c) => (
                  <TableCell key={`${s.titulo}-${c.chave}`} align="right" sx={{ top: 37 }}>
                    {c.nome}
                  </TableCell>
                )),
                <TableCell key={`${s.titulo}-total`} align="right" sx={{ top: 37 }}>
                  Total
                </TableCell>,
              ])}
            </TableRow>
          ) : null}
        </TableHead>
        <TableBody>
          {[...dias].reverse().map((dia) => (
            <TableRow key={dia} hover>
              <TableCell sx={num}>{format(parseISO(dia), 'dd/MM/yyyy')}</TableCell>
              {series.map((s) => {
                const ponto = s.pontos.find((p) => p.data === dia)
                const total = colunas.reduce((acc, c) => acc + valor(ponto, c.chave), 0)
                return [
                  ...colunas.map((c) => (
                    <TableCell key={`${s.titulo}-${c.chave}`} align="right" sx={num}>
                      {s.formatar(valor(ponto, c.chave))}
                    </TableCell>
                  )),
                  comTotal ? (
                    <TableCell key={`${s.titulo}-total`} align="right" sx={{ ...num, fontWeight: 600 }}>
                      {s.formatar(total)}
                    </TableCell>
                  ) : null,
                ]
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
